import { api } from '../api.ts';
import { html, mount, th } from '../html.ts';
import { formatDateTime, t, tk } from '../i18n.ts';
import { badge, emptyState } from '../ui.ts';
import { syncAlertState } from './sync-alert-state.ts';

interface SourceStatus {
  source: string;
  health: 'ok' | 'partial' | 'failed' | 'stale';
  last_run: {
    started_at: string | null;
    finished_at: string;
    expected_interval_minutes: number | null;
    counts: Record<string, number>;
    errors: string[];
  };
  last_ok_at: string | null;
  failed_last_24h: number;
}

const HEALTH: Record<SourceStatus['health'], [string, 'success' | 'warning' | 'danger']> = {
  ok: [tk('In Ordnung'), 'success'],
  partial: [tk('Mit Fehlern'), 'warning'],
  failed: [tk('Fehlgeschlagen'), 'danger'],
  stale: [tk('Veraltet'), 'danger'],
};

const SOURCE_NAMES: Record<string, string> = { 'purple-suite': tk('Purple Suite (Beads, Quality Console)') };

// Counters reported by the suite sync; unknown keys are shown as sent.
const COUNT_LABELS: Record<string, string> = {
  projectsCreated: tk('{n} Projekte angelegt'),
  tasksCreated: tk('{n} Aufgaben angelegt'),
  tasksUpdated: tk('{n} aktualisiert'),
  tasksUnchanged: tk('{n} unverändert'),
  kpiMeasurements: tk('{n} Messwerte'),
  failureTasksOpened: tk('{n} Testfehler geöffnet'),
  failureTasksResolved: tk('{n} Testfehler behoben'),
};

const sourceName = (source: string) => (SOURCE_NAMES[source] ? t(SOURCE_NAMES[source]) : source);

function countsLine(counts: Record<string, number>): string {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([key, n]) => (COUNT_LABELS[key] ? t(COUNT_LABELS[key], { n }) : `${key} ${n}`))
    .join(' · ');
}

function duration(run: SourceStatus['last_run']): string {
  if (!run.started_at) return '';
  const seconds = Math.max(0, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000));
  return t('{n} s', { n: seconds });
}

export async function loadSyncStatus(): Promise<SourceStatus[]> {
  return (await api.get<{ items: SourceStatus[] }>('/sync-runs/status')).items;
}

/** Settings card: one row per reporting source. */
export async function renderSyncSection(target: HTMLElement): Promise<void> {
  const items = await loadSyncStatus();
  mount(
    target,
    html`<div class="p-card__header"><h2 id="set-sync">${t('Synchronisationen')}</h2></div>
      <div class="p-card__body">
        <p class="pma-small">${th(t('Externe Abgleiche melden jeden Lauf über {endpoint}. Eine Quelle gilt als veraltet, wenn sie doppelt so lange wie erwartet nichts gemeldet hat.'), {
          endpoint: html`<code>POST /api/v1/sync-runs</code>`,
        })}</p>
        ${
          items.length
            ? html`<ul class="pma-list">${items.map((s) => {
                const [label, tone] = HEALTH[s.health];
                const run = s.last_run;
                const counts = countsLine(run.counts);
                return html`<li class="pma-list__row"><div><strong>${sourceName(s.source)}</strong> ${badge(t(label), tone)}
                  <div class="pma-muted pma-small">${t('Letzter Lauf {when}', { when: formatDateTime(run.finished_at) })}${run.started_at ? ` · ${duration(run)}` : ''}${
                    run.expected_interval_minutes ? ` · ${t('erwartet alle {n} min', { n: run.expected_interval_minutes })}` : ''
                  }</div>
                  ${counts ? html`<div class="pma-muted pma-small">${counts}</div>` : ''}
                  ${
                    run.errors.length
                      ? html`<ul class="pma-small pma-overdue">${run.errors.slice(0, 3).map((error) => html`<li class="pma-break">${error}</li>`)}${
                          run.errors.length > 3 ? html`<li>${t('… und {n} weitere', { n: run.errors.length - 3 })}</li>` : ''
                        }</ul>`
                      : ''
                  }
                  ${
                    s.health !== 'ok'
                      ? html`<div class="pma-muted pma-small">${t('Zuletzt fehlerfrei: {when}', { when: formatDateTime(s.last_ok_at) })}${
                          s.failed_last_24h ? ` · ${t('{n} Fehlläufe in 24 h', { n: s.failed_last_24h })}` : ''
                        }</div>`
                      : ''
                  }</div></li>`;
              })}</ul>`
            : emptyState(t('Noch keine Synchronisation gemeldet'), t('Der Purple-Suite-Abgleich meldet sich nach seinem nächsten Lauf hier.'))
        }
      </div>`,
  );
}

/** Dashboard hint when a source is failing or stale; renders nothing otherwise. */
export async function renderSyncAlert(target: HTMLElement): Promise<void> {
  const status = await syncAlertState(loadSyncStatus);
  if (status.kind === 'healthy') {
    target.hidden = true;
    return;
  }
  target.hidden = false;
  if (status.kind === 'unavailable') {
    mount(target, html`<div class="p-alert p-alert--warning" role="status"><span class="p-alert__icon" aria-hidden="true">!</span>
      <div>${t('Synchronisationsstatus derzeit nicht verfügbar.')} <a href="/settings?focus=sync">${t('Details in den Einstellungen')}</a></div></div>`);
    return;
  }
  const problems = status.problems;
  mount(
    target,
    html`<div class="p-alert p-alert--warning" role="status"><span class="p-alert__icon" aria-hidden="true">!</span>
      <div>${problems.map(
        (s) => html`<div>${t(s.health === 'stale' ? tk('{source}: seit {when} kein Lauf') : tk('{source}: letzter Lauf fehlgeschlagen'), {
          source: sourceName(s.source),
          when: formatDateTime(s.last_run.finished_at),
        })}</div>`,
      )}<a href="/settings?focus=sync">${t('Details in den Einstellungen')}</a></div></div>`,
  );
}
